import axios, { AxiosPromise } from 'axios';
import { instance } from '.';

const disaBaseUrl = 'http://dev.disa.dev4you.gmbh:6052';

export interface AssistantAnswer {
  answer: string;
  score: number;
  warning: string;
}

export interface FeedbackPayload {
  query: string;
  answer: string;
  projectId: string;
  roleId: string;
  userName?: string;
  feedbackQuality?: number;
  feedbackTypes?: number[];
  feedbackDetails?: string;
}

export interface BackendLlm {
  test(
    question: string,
    role: string,
    project: string,
    model: string
  ): AxiosPromise<AssistantAnswer>;
  ask(
    question: string,
    role: string,
    project: string,
    model: string
  ): AxiosPromise<AssistantAnswer>;
  streamChat(
    question: string,
    role: string,
    project: string,
    onChunk: (chunk: string) => void,
    signal?: AbortSignal
  ): Promise<void>;
  sendFeedback(payload: FeedbackPayload): AxiosPromise<void>;
}

export const defaultBackendLlm: BackendLlm = {
  test(
    question: string,
    role: string,
    project: string,
    model: string
  ): AxiosPromise<AssistantAnswer> {
    const url = `${disaBaseUrl}/api/Assistant/V3/${encodeURIComponent(
      question
    )}/${role}/${project}/${model}`;
    return axios.get<AssistantAnswer>(url);
  },
  ask(
    question: string,
    role: string,
    project: string,
    model: string
  ): AxiosPromise<AssistantAnswer> {
    const roleAppend = role ? `/${role}` : '';
    const url = `/api/Llm/ask/${project}${roleAppend}`;
    return instance.post(url, { Prompt: question, ModelId: model });
  },
  async streamChat(
    question: string,
    role: string,
    project: string,
    onChunk: (chunk: string) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const roleAppend = role ? `/${role}` : '';
    const url = `/api/Llm/stream/${project}${roleAppend}`;
    const token = localStorage.getItem('access_token');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ Prompt: question }),
      signal,
    });
    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.status}`);
    }
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const json = line.slice(6).trim();
        if (!json) continue;
        const chunk = JSON.parse(json);
        if (chunk.error) throw new Error(chunk.textResponse ?? 'Stream error');
        if (chunk.textResponse) {
          onChunk(chunk.textResponse);
          await new Promise((r) => setTimeout(r, 0));
        }
        if (chunk.close) return;
      }
    }
  },
  sendFeedback(payload: FeedbackPayload): AxiosPromise<void> {
    return instance.post('/api/Llm/feedback', payload);
  },
};
